import { useState, useRef, useEffect, useCallback } from 'react';

interface TranscriptionConfig {
  input_audio_transcription: {
    model: string;
    language: string;
    prompt?: string;
  };
  turn_detection: {
    type: string;
    threshold?: number;
    prefix_padding_ms?: number;
    silence_duration_ms?: number;
  };
  id?: string;
}

interface Event {
  type: string;
  session?: TranscriptionConfig;
  transcript?: string;
  delta?: string;
  item_id?: string;
  content_index?: number;
  error?: any;
}

class RTCSession {
  useToken: boolean;
  stream: MediaStream | null;
  peer: RTCPeerConnection | null;
  channel: RTCDataChannel | null;
  muted: boolean;
  isDevMode: boolean;
  onconnectionstatechange?: (state: string) => void;
  onmessage?: (parsed: Event) => void;
  ontrack?: (e: RTCTrackEvent) => void;
  onopen?: () => void;
  onerror?: (e: Error) => void;

  constructor() {
    this.useToken = true;
    this.stream = null;
    this.peer = null;
    this.channel = null;
    this.muted = false;
    this.isDevMode = process.env.REACT_APP_ENABLE_DEVTOOLS === 'true';
  }

  private devLog(...args: any[]) {
    if (this.isDevMode) {
      console.log(...args);
    }
  }

  private devError(...args: any[]) {
    if (this.isDevMode) {
      console.error(...args);
    }
  }

  async startTranscription(
    audioStream: MediaStream,
    config: TranscriptionConfig
  ): Promise<void> {
    await this.startInternal(
      audioStream,
      config,
      '/v1/realtime/transcription_sessions'
    );
  }

  stop(): void {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
    if (this.peer) {
      this.peer.close();
      this.peer = null;
    }
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
    this.muted = false;
  }

  async startInternal(
    audioStream: MediaStream,
    config: TranscriptionConfig,
    endpoint: string
  ): Promise<void> {
    this.stream = audioStream;
    this.peer = new RTCPeerConnection();
    this.peer.ontrack = (event) => this.ontrack?.(event);
    this.peer.addTrack(audioStream.getTracks()[0]);
    this.peer.onconnectionstatechange = () => {
      if (this.peer && this.onconnectionstatechange) {
        this.onconnectionstatechange(this.peer.connectionState);
      }
    };
    this.channel = this.peer.createDataChannel('');
    this.channel.onopen = () => this.onopen?.();
    this.channel.onmessage = (event) => {
      if (this.onmessage) {
        try {
          const parsed = JSON.parse(event.data);
          this.onmessage(parsed);
        } catch (err) {
          this.devError('Error parsing message:', err);
        }
      }
    };
    const offer = await this.peer.createOffer();
    await this.peer.setLocalDescription(offer);
    try {
      const answer = await this.signal(offer, config, endpoint);
      await this.peer.setRemoteDescription(answer);
    } catch (error) {
      const errorObj =
        error instanceof Error ? error : new Error(String(error));
      this.onerror?.(errorObj);
    }
  }

  async signal(
    offer: RTCSessionDescriptionInit,
    config: TranscriptionConfig,
    endpoint: string
  ): Promise<RTCSessionDescriptionInit> {
    let response: Response;

    if (this.useToken) {
      const sessionResponse = await fetch('/.netlify/functions/openai-realtime-proxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          path: endpoint,
          method: 'POST',
          headers: {
            'openai-beta': 'realtime-v1',
            'Content-Type': 'application/json',
          },
          body: config
        })
      });

      if (!sessionResponse.ok) {
        const errorText = await sessionResponse.text();
        throw new Error(`Failed to request session token: ${sessionResponse.statusText} - ${errorText}`);
      }

      const data = await sessionResponse.json();
      const secret = data.client_secret.value;

      this.devLog('Data received on first pass: ', data);

      const realtimeEndpoint = '/v1/realtime?model=gpt-4o-realtime-preview&intent=transcription';

      response = await fetch('/.netlify/functions/openai-realtime-proxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          path: realtimeEndpoint,
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${secret}`,
            'Content-Type': 'application/sdp',
          },
          body: offer.sdp
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        this.devError('Realtime connection failed:', response.status, errorText);
        throw new Error(`Failed to signal: ${response.statusText} - ${errorText}`);
      }
    } else {
      const realtimeEndpoint = '/v1/realtime?model=gpt-4o-realtime-preview&intent=transcription';

      const formDataObj = {
        session: JSON.stringify(config),
        sdp: offer.sdp || ''
      };

      response = await fetch('/.netlify/functions/openai-realtime-proxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          path: realtimeEndpoint,
          method: 'POST',
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          body: formDataObj
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        this.devError('Realtime connection failed (FormData):', response.status, errorText);
        throw new Error(`Failed to signal: ${response.statusText} - ${errorText}`);
      }
    }

    const sdpResponse = await response.text();
    this.devLog('Received SDP response:', sdpResponse.substring(0, 100) + '...');

    if (!sdpResponse.includes('v=0') || !sdpResponse.includes('o=')) {
      this.devError('Invalid SDP response received:', sdpResponse);
      throw new Error('Invalid SDP response from server');
    }

    return { type: 'answer', sdp: sdpResponse };
  }
}

interface STTState {
  micAvailable: boolean;
  micPermissionAllowed: boolean;
  listening: boolean;
  transcribing: boolean;
  connecting: boolean;
  errorOccurred: boolean;
  errorMsg: string;
  transcript: string;
  interimTranscript: string;
  start: () => Promise<void>;
  stop: () => void;
  clear: () => void;
}

const useSTT = (
  lang: string = 'en',
  prompt: string = ''
): STTState => {
  const [micAvailable, setMicAvailable] = useState<boolean>(true);
  const [micPermissionAllowed, setMicPermissionAllowed] = useState<boolean>(true);
  const [listening, setListening] = useState<boolean>(false);
  const [transcribing, setTranscribing] = useState<boolean>(false);
  const [connecting, setConnecting] = useState<boolean>(false);
  const [errorOccurred, setErrorOccurred] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [transcript, setTranscript] = useState<string>('');
  const [interimTranscript, setInterimTranscript] = useState<string>('');
  const sessionRef = useRef<RTCSession | null>(null);
  const processedRef = useRef<Set<string>>(new Set());

  // Add refs for the states that need to be accessed in callbacks
  const listeningRef = useRef<boolean>(false);
  const connectingRef = useRef<boolean>(false);

  const isDevMode = process.env.REACT_APP_ENABLE_DEVTOOLS === 'true';

  const devLog = (...args: any[]) => {
    if (isDevMode) {
      console.log(...args);
    }
  };

  const devError = (...args: any[]) => {
    if (isDevMode) {
      console.error(...args);
    }
  };

  // Sync state with refs
  useEffect(() => {
    listeningRef.current = listening;
  }, [listening]);

  useEffect(() => {
    connectingRef.current = connecting;
  }, [connecting]);

  const checkMic = async (): Promise<boolean> => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setMicAvailable(false);
        setErrorMsg('WebRTC is not supported by your browser.');
        setErrorOccurred(true);
        return false;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMicAvailable(true);
      setMicPermissionAllowed(true);
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (error: any) {
      setMicAvailable(true);
      if (
        error.name === 'NotAllowedError' ||
        error.name === 'PermissionDeniedError'
      ) {
        setMicPermissionAllowed(false);
        setErrorMsg('Microphone permission denied.');
        setErrorOccurred(true);
      } else if (
        error.name === 'NotFoundError' ||
        error.name === 'DevicesNotFoundError'
      ) {
        setMicAvailable(false);
        setErrorMsg('No microphone found.');
        setErrorOccurred(true);
      } else {
        setErrorMsg(`Error checking microphone: ${error.message}`);
        setErrorOccurred(true);
      }
      return false;
    }
  };

  const handleMsg = useCallback((event: Event): void => {
    devLog('Received WebRTC message:', event.type, event);

    switch (event.type) {
      case 'session.created':
        devLog('Session created, sending session update to disable responses');
        setConnecting(false);
        setListening(true);
        if (sessionRef.current?.channel?.readyState === 'open') {
          const sessionUpdate = {
            type: 'session.update',
            session: {
              modalities: ['text'],
              turn_detection: {
                type: 'server_vad',
                threshold: 0.3,
                prefix_padding_ms: 300,
                silence_duration_ms: 200,
                create_response: false
              },
              input_audio_transcription: {
                model: 'gpt-4o-transcribe',
                language: lang,
                prompt: prompt,
              }
            }
          };
          sessionRef.current.channel.send(JSON.stringify(sessionUpdate));
          devLog('Sent session update to disable responses');
        }
        break;

      case 'transcription_session.created':
        devLog('Transcription session created, stopping connecting state');
        setConnecting(false);
        setListening(true);
        break;
     
      case 'conversation.item.input_audio_transcription.delta':
        devLog('Transcription delta:', event.delta);
        if (event.delta) {
            setInterimTranscript(event.delta);
        }
        break;

      case 'conversation.item.input_audio_transcription.completed':
        devLog('Transcription completed:', event.transcript);
        if (event.transcript) {
          const id = `${Date.now()}_${event.transcript}`;
          if (!processedRef.current.has(id)) {
            processedRef.current.add(id);
            setTranscript((prev) => prev + event.transcript + ' ');
          }
        }
        setInterimTranscript('');
        break;

      case 'conversation.item.input_audio_transcription.failed':
        devError('Transcription failed:', event.error);
        setErrorMsg('Transcription failed');
        setErrorOccurred(true);
        setTranscribing(false);
        break;

      default:
        devLog('Unhandled message type:', event.type);
        break;
    }
  }, [lang, prompt, devLog, devError]);

  const handleErr = useCallback((error: Error): void => {
    devError(error);
    setErrorMsg(error.message);
    setErrorOccurred(true);
    setListening(false);
    setConnecting(false);
    setTranscribing(false);
    if (sessionRef.current) {
      sessionRef.current.stop();
      sessionRef.current = null;
    }
  }, [devError]);

  const start = useCallback(async (): Promise<void> => {
    // Use refs for immediate access to current state
    if (listeningRef.current || connectingRef.current) {
      devLog('Already listening or connecting, skipping start');
      return;
    }

    const available = await checkMic();
    if (!available) {
      return;
    }
   
    setTranscript('');
    setInterimTranscript('');
    setConnecting(true);
    setErrorOccurred(false);
    setErrorMsg('');
    processedRef.current.clear();

    sessionRef.current = new RTCSession();
    sessionRef.current.onconnectionstatechange = (state) => {
      devLog('WebRTC connection state changed:', state);
      if (state === 'connected') {
        devLog('WebRTC connected successfully');
        setConnecting(false);
        setListening(true);
      } else if (state === 'disconnected' || state === 'failed' || state === 'closed') {
        devLog('WebRTC connection failed or disconnected:', state);
        stop();
      }
    };
    sessionRef.current.onopen = () => {
      devLog('WebRTC data channel opened');
      setConnecting(false);
      setListening(true);
    };
    sessionRef.current.onmessage = handleMsg;
    sessionRef.current.onerror = handleErr;

    const config: TranscriptionConfig = {
      input_audio_transcription: {
        model: 'gpt-4o-transcribe',
        language: lang,
        prompt: prompt,
      },
      turn_detection: {
        type: 'server_vad',
        threshold: 0.3,
        prefix_padding_ms: 300,
        silence_duration_ms: 200,
      },
    };

    try {
      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      await sessionRef.current.startTranscription(micStream, config);
    } catch (error: any) {
      handleErr(error);
    }
  }, [lang, prompt, handleMsg, handleErr, devLog]);

  const stop = useCallback((): void => {
    if (sessionRef.current) {
      sessionRef.current.stop();
      sessionRef.current = null;
    }
    setListening(false);
    setConnecting(false);
    setTranscribing(false);
  }, []);

  const clear = useCallback((): void => {
    setTranscript('');
    setInterimTranscript('');
    processedRef.current.clear();
  }, []);

  useEffect(() => {
    return () => {
      if (sessionRef.current) {
        sessionRef.current.stop();
        sessionRef.current = null;
      }
    };
  }, []);

  return {
    micAvailable,
    micPermissionAllowed,
    listening,
    transcribing,
    connecting,
    errorOccurred,
    errorMsg,
    transcript,
    interimTranscript,
    start,
    stop,
    clear,
  };
};

export default useSTT;